import { Box, Grid, ListItemText, MenuItem, MenuList, Select, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import MainContainer from "../../../components/Layout/MainContainer";
import { performGetRequest } from "../../../services/apiServices/AxiosUtils";

import { Amenities } from "./BuildingBetterCourtsData/Amenities";
import { BarrierFreeAccess } from "./BuildingBetterCourtsData/BarrierFreeAccess";
import { CaseDisplay } from "./BuildingBetterCourtsData/CaseDisplay";
import { GettingThere } from "./BuildingBetterCourtsData/GettingThere";
import { Hygiene } from "./BuildingBetterCourtsData/Hygiene";
import { LitigantProfile } from "./BuildingBetterCourtsData/LitigantProfile";
import { Navigation } from "./BuildingBetterCourtsData/Navigation";
import { Overview } from "./BuildingBetterCourtsData/Overview";
import { Security } from "./BuildingBetterCourtsData/Security";
import { Snapshot } from "./BuildingBetterCourtsData/Snapshot";
import { WaitingArea } from "./BuildingBetterCourtsData/WaitingArea";
import { Website } from "./BuildingBetterCourtsData/Website";

import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import courtImg from "../../../assets/SvgIcons/ico-courts.svg";
import demography from "../../../assets/SvgIcons/ico-demographics.svg";
import judgeImg from "../../../assets/SvgIcons/ico-judges.svg";
import pendency from "../../../assets/SvgIcons/ico-pendency.svg";
import judgeVacancy from "../../../assets/SvgIcons/ico-vacancy.svg";
import allStateMap from "../../../assets/Images/bbc-all-state-map.png";
import CustomBuildingNavBar from "../../../components/HorizontalBars/CustomBuildingNavBar";
import useCustomLogic from "../../../Hooks/UseCustomLogic";
import Card from "./Common/Card";
import Drawer from "./Common/Drawer";
import legacyBuildingBetterCourtData from "./legacyBuildingBetterCourtData.json";
import { legacySnapshotMedia } from "./legacySnapshotMedia";

import { mapMenuObjState, menu } from "../../../utils/Constants";
import styled from "styled-components";

const BBC_IMAGE_BASE_URL =
  typeof window !== "undefined" ? window.location.origin : "https://jaldi-vidhilegalpolicy.in";

let initialCardData = [
  {
    icon: pendency,
    content: "Case Pendency",
    statistics: "2,77,52,879",
  },
  {
    icon: judgeVacancy,
    content: "Judge Vacancy",
    statistics: "5676",
  },
  {
    icon: courtImg,
    content: "No. of Districts Court Complexes",
    statistics: "665",
  },
  {
    icon: demography,
    content: "No. Of Judges",
    statistics: "17028",
  },
  {
    icon: judgeImg,
    content: "Litigants Surveyed",
    statistics: "6650",
  },
];

const BuildingBetterCourtsData = () => {
  const { navigate, isXs, isXss, isMd, theme, isSm, dispatch, loader } = useCustomLogic();
  const [dropdownState, setDropdownState] = useState("All");
  const [dropdownDistrict, setDropdownDistrict] = useState("Select District");
  const [snapshotDistrictImage, setSnapshotDistrictImage] = useState("");
  const [selectedMenu, setSelectedMenu] = useState("Snapshot");
  const [statesList, setStatesList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const filteredDistrictData = districtList?.filter((item) => item.district_name !== "All");
  const [stateData, setStateData] = useState({});
  const [districtData, setDistrictData] = useState({});
  const [cardData, setCardData] = useState(initialCardData);
  const [isMenuDisabled, setIsMenuDisabled] = useState(true);
  const [keyStatsByState, setKeyStatsByState] = useState({});
  const [rawStateRows, setRawStateRows] = useState([]);

  const extractRows = (res) => {
    const data = res?.data?.data ?? res?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const isBlankValue = (value) =>
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");

  const rowKey = (item = {}) => `${item?.state || ""}||${item?.district_name || ""}`;

  const flattenRowsForLookup = (rows = []) =>
    rows.flatMap((item) => {
      const { districts_name: _districtsName, ...parentRow } = item || {};
      const districts = Array.isArray(item?.districts_name)
        ? item.districts_name.map((district) => ({
            state: parentRow.state,
            ...district,
          }))
        : [];
      return [parentRow, ...districts];
    });

  const mergeRowWithFallback = (row = {}, fallback = {}) => {
    const merged = { ...fallback, ...row };
    Object.keys(fallback || {}).forEach((key) => {
      if (isBlankValue(row?.[key]) && !isBlankValue(fallback?.[key])) {
        merged[key] = fallback[key];
      }
    });
    return merged;
  };

  const mergeRowsWithFallback = (rows = [], fallbackRows = []) => {
    if (!fallbackRows.length) return rows;

    const fallbackLookup = flattenRowsForLookup(fallbackRows).reduce((acc, item) => {
      const key = rowKey(item);
      if (key !== "||") acc[key] = item;
      return acc;
    }, {});

    const rowsHaveDistrictGroups = rows.some((item) => Array.isArray(item?.districts_name));
    if (rowsHaveDistrictGroups) {
      const seen = new Set();
      const mergedRows = rows.map((item) => {
        const key = rowKey(item);
        seen.add(key);
        const mergedItem = mergeRowWithFallback(item, fallbackLookup[key]);
        if (!Array.isArray(item?.districts_name)) return mergedItem;

        return {
          ...mergedItem,
          districts_name: item.districts_name.map((district) => {
            const districtKey = rowKey({ state: item?.state, district_name: district?.district_name });
            seen.add(districtKey);
            return mergeRowWithFallback(district, fallbackLookup[districtKey]);
          }),
        };
      });

      fallbackRows.forEach((item) => {
        const key = rowKey(item);
        if (!seen.has(key)) mergedRows.push(item);
      });

      return mergedRows;
    }

    const seen = new Set();
    const mergedRows = rows.map((item) => {
      const key = rowKey(item);
      seen.add(key);
      return mergeRowWithFallback(item, fallbackLookup[key]);
    });

    flattenRowsForLookup(fallbackRows).forEach((item) => {
      const key = rowKey(item);
      if (key !== "||" && !seen.has(key)) {
        seen.add(key);
        mergedRows.push(item);
      }
    });

    return mergedRows;
  };

  const buildKeyStatsLookup = (rows = []) =>
    rows.reduce((acc, item) => {
      if (item?.state) {
        acc[item.state] = item;
      }
      return acc;
    }, {});

  const hasKeyStats = (stats = {}) =>
    Boolean(
      stats?.case_pendency ||
        stats?.judge_vacancy ||
        stats?.no_of_districts_court_complexes ||
        stats?.no_of_judges ||
        stats?.litigants_surveyed
    );

  const keyStatsFor = (state, existingStats = {}) =>
    hasKeyStats(existingStats) ? existingStats : keyStatsByState[state] || {};

  const mediaPath = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${BBC_IMAGE_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const stateImageFor = (state) =>
    state === "All"
      ? allStateMap
      : state
      ? mediaPath(`/images/StateMapImages/${state}.svg`)
      : "";

  const districtImageFor = (state, district) => {
    if (!state || !district) return "";

    const stateFolder = state.replace(/\s+/g, "");
    const districtFile = district.replace(/\s+/g, "_");
    return mediaPath(`/images/Courts_District/${stateFolder}/${districtFile}.jpg`);
  };

  const legacyStateImageFor = (state) => {
    return mediaPath(legacySnapshotMedia.stateImages[state]);
  };

  const legacyDistrictImageFor = (state, district) => {
    return mediaPath(legacySnapshotMedia.districtImages[`${state}|${district}`]);
  };

  const stateImageForRow = (item) => {
    if (item?.state === "All") return stateImageFor(item.state);
    return (
      mediaPath(item?.state_image) ||
      legacyStateImageFor(item?.state) ||
      stateImageFor(item?.state)
    );
  };

  const districtImageForRow = (state, district) =>
    mediaPath(district?.link) ||
    legacyDistrictImageFor(state, district?.district_name) ||
    districtImageFor(state, district?.district_name);

  const normalizeStateRows = (rows = []) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (!safeRows.length) return [];

    if (safeRows.some((item) => Array.isArray(item?.districts_name))) {
      return safeRows.map((item) => ({
        ...item,
        state_image: stateImageForRow(item),
        key_stats: keyStatsFor(item?.state, item?.key_stats),
        districts_name: (item?.districts_name || []).map((district) => ({
          ...district,
          detail: district?.detail || district,
          link: districtImageForRow(item?.state, district),
        })),
      }));
    }

    if (selectedMenu === "Litigant Profile") {
      return normalizeLitigantRows(safeRows);
    }

    const groups = safeRows.reduce((acc, item) => {
      const state = item?.state || "All";
      if (!acc[state]) acc[state] = [];
      acc[state].push(item);
      return acc;
    }, {});

    const stateNames = Object.keys(groups).sort((a, b) => {
      if (a === "All") return -1;
      if (b === "All") return 1;
      return a.localeCompare(b);
    });

    return stateNames.map((state) => {
      const stateRows = groups[state] || [];
      const summaryRow =
        stateRows.find((item) => item?.district_name === "All") || stateRows[0] || {};
      const districtRows = Object.values(
        stateRows
          .filter((item) => item?.district_name && item.district_name !== "All")
          .reduce((acc, item) => {
            if (!acc[item.district_name]) {
              acc[item.district_name] = {
                ...item,
                detail: item,
                link:
                  mediaPath(item?.link) ||
                  legacyDistrictImageFor(state, item.district_name) ||
                  districtImageFor(state, item.district_name),
              };
            }
            return acc;
          }, {})
      );

      return {
        ...summaryRow,
        id: summaryRow?.id ?? state,
        state,
        state_image:
          state === "All"
            ? stateImageFor(state)
            : summaryRow?.state_image
            ? mediaPath(summaryRow.state_image)
            : legacyStateImageFor(state)
            ? legacyStateImageFor(state)
            : stateImageFor(state),
        key_stats: keyStatsFor(state, summaryRow?.key_stats),
        districts_name: districtRows,
      };
    });
  };

  const normalizeGender = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized.startsWith("m")) return "male";
    if (normalized.startsWith("f")) return "female";
    return "";
  };

  const normalizeEducation = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return "";
    if (normalized.includes("illiterate")) return "illiterate";
    if (normalized.includes("basic") || normalized.includes("formal")) return "basic_literacy";
    if (normalized.includes("school") || normalized.includes("primary") || normalized.includes("secondary")) {
      return "complete_schooling";
    }
    if (normalized.includes("graduate")) return "graduate";
    return "";
  };

  const normalizeAgeKey = (value) => {
    const age = Number.parseInt(value, 10);
    if (!Number.isFinite(age)) return "";
    if (age >= 18 && age <= 25) return "18_to_25";
    if (age >= 26 && age <= 35) return "26_to_35";
    if (age >= 36 && age <= 45) return "36_to_45";
    if (age >= 46 && age <= 55) return "46_to_55";
    if (age >= 56 && age <= 65) return "56_to_65";
    if (age >= 66 && age <= 75) return "66_to_75";
    if (age >= 76) return "76_to_85";
    return "";
  };

  const percent = (part, total) => (total ? Math.round((part / total) * 100) : 0);

  const buildLitigantSummary = (state, stateRows = []) => {
    const summaryRow =
      stateRows.find((item) => item?.district_name === "All") || stateRows[0] || {};
    const litigantRows = stateRows.filter(
      (item) => item?.district_name !== "All" && (item?.age || item?.gender || item?.education)
    );
    const counts = {
      male: 0,
      female: 0,
      male_illiterate: 0,
      male_basic_literacy: 0,
      male_complete_schooling: 0,
      male_graduate: 0,
      female_illiterate: 0,
      female_basic_literacy: 0,
      female_complete_schooling: 0,
      female_graduate: 0,
    };
    const ageKeys = ["18_to_25", "26_to_35", "36_to_45", "46_to_55", "56_to_65", "66_to_75", "76_to_85"];
    ageKeys.forEach((key) => {
      counts[`male_${key}`] = 0;
      counts[`female_${key}`] = 0;
    });

    litigantRows.forEach((row) => {
      const gender = normalizeGender(row?.gender);
      if (!gender) return;
      counts[gender] += 1;
      const ageKey = normalizeAgeKey(row?.age);
      if (ageKey) counts[`${gender}_${ageKey}`] += 1;
      const education = normalizeEducation(row?.education);
      if (education) counts[`${gender}_${education}`] += 1;
    });

    const total = counts.male + counts.female;
    const ratioFallback = {
      male_ratio: percent(counts.male, total),
      female_ratio: percent(counts.female, total),
      male_illiterate: percent(counts.male_illiterate, total),
      male_basic_literacy: percent(counts.male_basic_literacy, total),
      male_complete_schooling: percent(counts.male_complete_schooling, total),
      male_graduate: percent(counts.male_graduate, total),
      female_illiterate: percent(counts.female_illiterate, total),
      female_basic_literacy: percent(counts.female_basic_literacy, total),
      female_complete_schooling: percent(counts.female_complete_schooling, total),
      female_graduate: percent(counts.female_graduate, total),
    };

    return {
      ...summaryRow,
      ...counts,
      ...Object.fromEntries(
        Object.entries(ratioFallback).map(([key, value]) => [
          key,
          summaryRow?.[key] ?? value,
        ])
      ),
      id: summaryRow?.id ?? state,
      state,
      district_name: "All",
      key_stats: keyStatsFor(state, summaryRow?.key_stats),
    };
  };

  const normalizeLitigantRows = (rows = []) => {
    const groups = rows.reduce((acc, item) => {
      const state = item?.state || "All";
      if (!acc[state]) acc[state] = [];
      acc[state].push(item);
      if (state !== "All" && item?.district_name && item.district_name !== "All") {
        if (!acc.All) acc.All = [];
        acc.All.push(item);
      }
      return acc;
    }, {});

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === "All") return -1;
        if (b === "All") return 1;
        return a.localeCompare(b);
      })
      .map((state) => {
        const stateRows = groups[state] || [];
        const districtRows = Object.values(
          stateRows
            .filter((item) => item?.district_name && item.district_name !== "All")
            .reduce((acc, item) => {
              const district = item.district_name;
              const districtKey = state === "All" ? `${item?.state || state}-${district}` : district;
              if (!acc[districtKey]) {
                acc[districtKey] = {
                  id: `${state}-${districtKey}`,
                  state: state === "All" ? item?.state || state : state,
                  district_name: district,
                  detail: [],
                };
              }
              acc[districtKey].detail.push(item);
              return acc;
            }, {})
        );
        return {
          ...buildLitigantSummary(state, stateRows),
          districts_name: districtRows,
        };
      });
  };

  const applySelectedStateData = (rows, state, district) => {
    const normalizedRows = normalizeStateRows(rows);
    if (!normalizedRows.length) return;

    const selectedStateRow =
      normalizedRows.find((item) => item?.state === state) ||
      normalizedRows.find((item) => item?.state === "All") ||
      normalizedRows[0] ||
      {};
    const selectedStateName = selectedStateRow?.state || state;

    setStatesList(normalizedRows);
    if (selectedStateName && selectedStateName !== state) {
      setDropdownState(selectedStateName);
    }
    setStateData(
      selectedMenu == "Overview" && selectedStateName == "All" ? normalizedRows : selectedStateRow
    );
    setDistrictList(selectedStateRow?.districts_name ?? []);

    const currentDistrict =
      district !== "Select District"
        ? selectedStateRow?.districts_name?.find(
            (item) => item.district_name === district?.district_name
          )
        : null;

    setDropdownDistrict(currentDistrict ?? "Select District");
    setDistrictData(currentDistrict ?? {});

    if (selectedMenu === "Snapshot") {
      setSnapshotDistrictImage(currentDistrict?.link ?? "");
    }

    const keyStats = selectedStateRow?.key_stats || {};
    const cardArray = [
      keyStats?.case_pendency,
      keyStats?.judge_vacancy,
      keyStats?.no_of_districts_court_complexes,
      keyStats?.no_of_judges,
      keyStats?.litigants_surveyed,
    ];

    setCardData(
      initialCardData.map((item, index) => ({
        ...item,
        statistics: cardArray[index] || item.statistics || 0,
      }))
    );
  };

  //effects for api callings
  useEffect(() => {
    getStateDataMenuItemWise();
  }, [selectedMenu]);

  useEffect(() => {
    dispatch({
      type: "GET_BUILDING_BETTER_DATA",
      path: "/building_better_courts/key_states",
      isBuildingBetter: true,
      successCallBack: (res) => {
        setKeyStatsByState(buildKeyStatsLookup(extractRows(res)));
      },
    });
  }, []);

  useEffect(() => {
    if (rawStateRows.length) {
      applySelectedStateData(rawStateRows, dropdownState, dropdownDistrict);
    }
  }, [keyStatsByState]);

  // state wise data api calling function
  const getStateDataMenuItemWise = () => {
    let path = `/building_better_courts/${mapMenuObjState[selectedMenu]}`;
    const fallbackRows = legacyBuildingBetterCourtData[mapMenuObjState[selectedMenu]] || [];
    if (fallbackRows.length) {
      successCallBackState({ data: { data: fallbackRows } });
    }

    performGetRequest(path, true)
      .then((res) => {
        const rows = extractRows(res);
        if (rows.length) {
          successCallBackState({ data: { data: mergeRowsWithFallback(rows, fallbackRows) } });
        }
      })
      .catch(() => {
        if (!fallbackRows.length) setIsMenuDisabled(true);
      });
  };

  const successCallBackState = (res) => {
    const rows = extractRows(res);
    if (!rows.length) return;

    setRawStateRows(rows);
    applySelectedStateData(rows, dropdownState, dropdownDistrict);
    setIsMenuDisabled(false);
  };

  const handleChangeState = (event) => {
    let val = event.target.value;
    setDropdownState(val);
    setDropdownDistrict("Select District"); // on selecting other state setDistrict to Select District

    if (statesList) {
      applySelectedStateData(statesList, val, "Select District");
    }
  };

  const handleChangeDistrict = (event) => {
    let val = event.target.value;
    setDropdownDistrict(val);
    setDistrictData(val);

    if (selectedMenu === "Snapshot") {
      if (val !== "Select District") {
        setSnapshotDistrictImage(val?.link || "");
      }
    }
  };

  const handleMenuSelect = (val) => {
    setSelectedMenu(val);
    if (val == "Overview") {
      setDropdownDistrict("Select District");
    }
  };

  return (
    <>
      <Box
        sx={{
          padding: isXs ? "0px" : "16px 65px",
          position: "sticky",
          zIndex: "8",
          top: "60px",
          ...mainContainerStyle(isSm),
          display: "flex",
          justifyContent: isXs ? "flex-end" : "center",
        }}
      >
        <Grid
          container
          spacing={2}
          xs={12}
          sm={12}
          md={12}
          lg={12}
          sx={{ flexDirection: "row-reverse" }}
        >
          <Grid item xs={12} sm={12} md={7} lg={6} sx={{ paddingLeft: "0px !important" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: isSm ? "center" : "flex-end",
              }}
            >
              <CustomBuildingNavBar selectedTab={"data"} />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            lg={6}
            md={5}
            sx={{
              paddingRight: isXs ? "10px" : "0px",
              paddingBottom: isXs ? "10px" : "0px",
            }}
          >
            <Box
              display={"flex"}
              justifyContent={isSm ? "center" : "flex-start"}
              sx={{ width: "100%", gap: "10px" }}
            >
              {isXs && <Drawer selectedMenu={selectedMenu} setSelectedMenu={setSelectedMenu} />}

              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={dropdownState}
                inputProps={{ "aria-label": "Select state" }}
                onChange={handleChangeState}
                sx={{
                  height: "40px",
                  width: isXss ? "37%" : isXs ? "60%" : "40%",
                  background: "#ffffff",
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {statesList?.map((state) => (
                  <MenuItem value={state.state} key={state.id}>
                    {state.state}
                  </MenuItem>
                ))}
              </Select>

              {selectedMenu !== "Overview" && (
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={dropdownDistrict}
                  inputProps={{ "aria-label": "Select district" }}
                  onChange={handleChangeDistrict}
                  sx={{
                    height: "40px",
                    width: isXss ? "37%" : isXs ? "60%" : "40%",
                    background: "#ffffff",
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: "300px",
                      },
                    },
                  }}
                >
                  <MenuItem value={"Select District"}>Select District</MenuItem>

                  {/* <MenuItem value={"All Districts"}>All Districts</MenuItem> */}

                  {filteredDistrictData.map((district) => (
                    <MenuItem value={district} key={district.id}>
                      {district.district_name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* <Box>
        {dropdownDistrict === "Select District" ? (
          <p>state : {JSON.stringify(stateData)}</p>
        ) : (
          <p>District {JSON.stringify(districtData)}</p>
        )}
      </Box> */}

      {/* <LinkBar title={"Building Better Courts"}/> */}
      <MainContainer>
        <Grid container spacing={2} lg={12} md={12} sx={{ py: "20px" }}>
          {!isXs && (
            <Grid item spacing={2} xs={12} sm={12} md={2} lg={2}>
              <Box sx={{ width: 320, maxWidth: "100%" }}>
                <MenuList>
                  {menu
                    .filter(
                      (data) => !(dropdownDistrict !== "Select District" && data === "Overview")
                    )
                    .map((data) => (
                      <MenuItem
                        disabled={isMenuDisabled}
                        key={data}
                        onClick={() => handleMenuSelect(data)}
                      >
                        <ListItemText>
                          <Typography
                            variant={"body1"}
                            sx={{
                              color: data === selectedMenu && "#DC002D",
                              textDecoration: data === selectedMenu && "underline",
                            }}
                          >
                            {data}
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                    ))}
                </MenuList>
              </Box>
            </Grid>
          )}

          <Grid
            item
            spacing={2}
            xs={12}
            sm={12}
            md={selectedMenu === "Overview" ? 10 : 7}
            lg={selectedMenu === "Overview" ? 10 : 7}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                marginLeft: isMd && "13px",
                marginTop: "13px",
              }}
            >
              <Typography variant="regular1" sx={{ color: "#DC002D" }}>
                {selectedMenu}
              </Typography>
              <KeyboardArrowRightIcon />
              <Typography variant="regular1" sx={{ color: theme.palette.grey[400] }}>
                {dropdownDistrict !== "Select District"
                  ? dropdownDistrict.district_name
                  : dropdownState}
                {/* {dropdownState === "All States" ? "All" : dropdownState} */}
              </Typography>
            </Box>
            <Grid
              item
              spacing={2}
              xs={12}
              sm={12}
              md={12}
              lg={12}
              sx={{
                mt: "40px",
                ml: isMd && "12px",
                mr: isMd && "28px",
              }}
            >
              {selectedMenu === "Snapshot" && (
                <Snapshot
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  imageLink={snapshotDistrictImage}
                />
              )}

              {selectedMenu === "Litigant Profile" && (
                <LitigantProfile
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData?.detail}
                />
              )}
              {selectedMenu === "Getting There" && (
                <GettingThere
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Navigation" && (
                <Navigation
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Waiting Area" && (
                <WaitingArea
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Hygiene" && (
                <Hygiene
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Barrier-free Access" && (
                <BarrierFreeAccess
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Case Display" && (
                <CaseDisplay
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Amenities" && (
                <Amenities
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Security" && (
                <Security
                  selectedDistrict={dropdownDistrict}
                  dataState={stateData}
                  dataDistrict={districtData}
                />
              )}
              {selectedMenu === "Website" && (
                <Website
                  selectedDistrict={dropdownDistrict}
                  dataDistrict={districtData}
                  stateData={stateData}
                />
              )}
              {selectedMenu === "Overview" && (
                <Overview
                  selectedDistrict={dropdownDistrict}
                  dataDistrict={districtData}
                  stateData={stateData}
                  dropdownState={dropdownState}
                />
              )}
            </Grid>
          </Grid>

          {dropdownDistrict === "Select District" && selectedMenu !== "Overview" && (
            <Grid item xs={12} sm={12} md={3} lg={3} mt={5}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: "20px",
                  mb: "20px",
                  backgroundColor: "#F1f1f1",
                  padding: "5px 10px",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body1" sx={{ color: "#6c757d" }}>
                  KEY STATS
                </Typography>
                <ExpandCircleDownIcon
                  sx={{
                    fontSize: ".975rem",
                    color: "#495057 !important",
                    cursor: "pointer",
                  }}
                />
              </Box>
              <Box>
                {cardData &&
                  cardData.map((elem, index) => {
                    return (
                      <Box sx={{ mt: "30px" }}>
                        <Card
                          key={index}
                          imgSrc={elem.icon}
                          content={elem.content}
                          statistics={elem.statistics ?? 0}
                        />
                      </Box>
                    );
                  })}
              </Box>
            </Grid>
          )}
        </Grid>
      </MainContainer>
    </>
  );
};

export default BuildingBetterCourtsData;

const mainContainerStyle = (isSm) => {
  return {
    // height: isSm ? "120px" : "0px
    justifyContent: "center",
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.80)",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.08)",
    backdropFilter: "blur(2px)",
    position: "sticky",
    zIndex: 2,
  };
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(20, 12px); /* 20 columns */
  grid-template-rows: repeat(5, 12px); /* 5 rows for 100 blocks */
  grid-gap: 3px;
  margin-top: 25px;
  place-content: center;
`;

const Block = styled.div`
  width: 12px;
  height: 12px;
  background-color: ${({ filled, color }) => (filled ? color : "#ccc")};
  border-radius: 1px;
`;

export const BlockGridGraph = ({ percentage = 0, color = "#00bcd4" }) => {
  const parsedNum = parseInt(percentage);
  const totalBlocks = 100;
  const filledBlocks = Math.round((parsedNum / 100) * totalBlocks);

  const rows = 5;
  const cols = 20;

  const blocks = Array.from({ length: rows * cols });

  return (
    <GridWrapper>
      {blocks.map((_, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const bottomToTopRow = rows - 1 - row;
        const columnWiseIndex = bottomToTopRow + col * rows;
        const filled = columnWiseIndex < filledBlocks;
        return <Block key={i} filled={filled} color={color} />;
      })}
    </GridWrapper>
  );
};
